import { lazy } from 'react';
import { Navigate } from "react-router-dom";
import Loadable from "../layouts/loader/Loadable";
import OAuthPopup from "../oauth/OAuth2Popup";
import CommissionSlabDetails from "../components/zone/CommissionSlabs";
import CommissionRuleDetails from "../components/zone/CommissionSlabDetail";
import PackageDetails from '../components/package/PackageDetails';
import OrphanInvoices from '../components/report/OrphanInvoices';
import PackageAdd from '../components/package/PackageAdd';

/****Layouts*****/
const FullLayout = Loadable(lazy(() => import("../layouts/FullLayout")));
const BlankLayout = Loadable(lazy(() => import("../layouts/BlankLayout")));
/***** Pages ****/

// Notification Components
const NotificationDashboard = Loadable(lazy(() => import("../views/notifications/NotificationDashboard")));
const NotificationTemplates = Loadable(lazy(() => import("../views/notifications/NotificationTemplates")));
const NotificationRoutes = Loadable(lazy(() => import("../views/notifications/NotificationRoutes")));
const NotificationEmailConfig = Loadable(lazy(() => import("../views/notifications/NotificationEmailConfig")));
const NotificationSmsConfig = Loadable(lazy(() => import("../views/notifications/NotificationSmsConfig")));
const NotificationPushConfig = Loadable(lazy(() => import("../views/notifications/NotificationPushConfig")));
const NotificationSend = Loadable(lazy(() => import("../views/notifications/NotificationSend")));
const NotificationTracking = Loadable(lazy(() => import("../views/notifications/NotificationTracking")));

const Company = Loadable(lazy(() => import("../components/company/Company")));
const CompanyDetails = Loadable(lazy(() => import("../components/company/CompanyDetails")));
const CompanyList = Loadable(lazy(() => import("../components/company/CompanyList")));

const DistributorList = Loadable(lazy(() => import("../components/distributor/DistributorList")));
const DistributorDetails = Loadable(lazy(() => import("../components/distributor/DistributorDetails")));
const Distributor = Loadable(lazy(() => import("../components/distributor/Distributor")));

const ServerGroupList = Loadable(lazy(() => import("../components/serverGroup/ServerGroupList")));
const ServerGroupDetails = Loadable(lazy(() => import("../components/serverGroup/ServerGroupDetails")));
const ServerGroup = Loadable(lazy(() => import("../components/serverGroup/ServerGroup")));

const ZoneGroupList = Loadable(lazy(() => import("../components/zoneGroup/ZoneGroupList")));
const ZoneGroupDetails = Loadable(lazy(() => import("../components/zoneGroup/ZoneGroupDetails")));
const ZoneGroup = Loadable(lazy(() => import("../components/zoneGroup/ZoneGroup")));

const ZoneList = Loadable(lazy(() => import("../components/zone/ZoneList")));
const ZoneAdd = Loadable(lazy(() => import("../components/zone/ZoneAdd")));
const ZoneDetails = Loadable(lazy(() => import("../components/zone/ZoneDetails")));
const ZoneEdit = Loadable(lazy(() => import("../components/zone/ZoneEdit")));
const ZoneStatement = Loadable(lazy(() => import("../components/zone/ZoneStatement")));
const ZoneCommission = Loadable(lazy(() => import("../components/zone/ZoneCommission")));
const ZoneCommissionV2 = Loadable(lazy(() => import("../components/zone/ZoneCommissionV2")));
const ZoneRenew = Loadable(lazy(() => import("../components/zone/ZoneRenew")));
const ZoneBalance = Loadable(lazy(() => import("../components/zone/ZoneBalance")));
const AllZoneBalance = Loadable(lazy(() => import("../components/zone/AllZoneBalance")));
const ZoneAdjustment = Loadable(lazy(() => import("../components/zone/ZoneAdjustment")));
const ZoneWalletRecharge = Loadable(lazy(() => import("../components/zone/ZoneWalletRecharge")));
const ZoneTransactions = Loadable(lazy(() => import("../components/zone/ZoneTransactionHistory")));
const CustomerSearch = Loadable(lazy(() => import("../components/customer/CustomerSearch")));
const AssignedPackagesList = Loadable(lazy(() => import("../components/nas/AssignedPackagesList")));

const RenewalTransactions = Loadable(lazy(() => import("../components/report/RenewalTransactions")));
const TopupTransactions = Loadable(lazy(() => import("../components/report/TopupTransactions")));

const NasList = Loadable(lazy(() => import("../components/nas/NasList")));
const NasAdd = Loadable(lazy(() => import("../components/nas/NasAdd")));
const NasEdit = Loadable(lazy(() => import("../components/nas/NasEdit")));
const NasZoneMappingList = Loadable(lazy(() => import("../components/nas/NasZoneMappingList")));
const NasPackageZoneMappingList = Loadable(lazy(() => import("../components/nas/NasPackageZoneMappingList")));

// VAS (Value-Added Services) Module Components
const ValueAddedServiceList = Loadable(lazy(() => import("../components/vas/services/ValueAddedServiceList")));
const ValueAddedServiceAdd = Loadable(lazy(() => import("../components/vas/services/ValueAddedServiceAdd")));
const ValueAddedServiceEdit = Loadable(lazy(() => import("../components/vas/services/ValueAddedServiceEdit")));

const VASProviderPackageList = Loadable(lazy(() => import("../components/vas/providers/VASProviderPackageList")));
const VASProviderPackageAdd = Loadable(lazy(() => import("../components/vas/providers/VASProviderPackageAdd")));
const VASProviderPackageEdit = Loadable(lazy(() => import("../components/vas/providers/VASProviderPackageEdit")));

const VASAggregateList = Loadable(lazy(() => import("../components/vas/aggregates/VASAggregateList")));
const VASAggregateAdd = Loadable(lazy(() => import("../components/vas/aggregates/VASAggregateAdd")));
const VASAggregateEdit = Loadable(lazy(() => import("../components/vas/aggregates/VASAggregateEdit")));

const BundleList = Loadable(lazy(() => import("../components/vas/bundles/BundleList")));
const BundleAdd = Loadable(lazy(() => import("../components/vas/bundles/BundleAdd")));
const BundleEdit = Loadable(lazy(() => import("../components/vas/bundles/BundleEdit")));
const BundleConfigure = Loadable(lazy(() => import("../components/vas/bundles/BundleConfigure")));

const AutoEligibilityList = Loadable(lazy(() => import("../components/vas/eligibility/AutoEligibilityList")));
const AutoEligibilityAdd = Loadable(lazy(() => import("../components/vas/eligibility/AutoEligibilityAdd")));
const AutoEligibilityEdit = Loadable(lazy(() => import("../components/vas/eligibility/AutoEligibilityEdit")));

const OttCustomerList = Loadable(lazy(() => import("../components/vas/customers/CustomerList")));
const ManualConsole = Loadable(lazy(() => import("../components/vas/manual-console/ManualConsole")));

const CouponList = Loadable(lazy(() => import("../components/vas/coupons/CouponList")));
const CouponUpload = Loadable(lazy(() => import("../components/vas/coupons/CouponUpload")));

const SourceConfigList = Loadable(lazy(() => import("../components/vas/source-config/SourceConfigList")));
const SourceConfigAdd = Loadable(lazy(() => import("../components/vas/source-config/SourceConfigAdd")));
const SourceConfigEdit = Loadable(lazy(() => import("../components/vas/source-config/SourceConfigEdit")));

const AuditLogList = Loadable(lazy(() => import("../components/vas/audit/AuditLogList")));
const EntityAuditHistory = Loadable(lazy(() => import("../components/vas/audit/EntityAuditHistory")));

const CommissionRuleList = Loadable(lazy(() => import("../components/zone/CommissionRuleList")));

const LegalOfficeList = Loadable(lazy(() => import("../components/legalOffice/LegalOfficeList")));
const LegalOfficeDetails = Loadable(lazy(() => import("../components/legalOffice/LegalOfficeDetails")));
const LegalOffice = Loadable(lazy(() => import("../components/legalOffice/LegalOffice")));

const SupportOfficeList = Loadable(lazy(() => import("../components/supportOffice/SupportOfficeList")));
const SupportOfficeDetails = Loadable(lazy(() => import("../components/supportOffice/SupportOfficeDetails")));
const SupportOffice = Loadable(lazy(() => import("../components/supportOffice/SupportOffice")));

const ServerList = Loadable(lazy(() => import("../components/server/ServerList")));
const ServerDetails = Loadable(lazy(() => import("../components/server/ServerDetails")));
const Server = Loadable(lazy(() => import("../components/server/Server")));

const PackageList = Loadable(lazy(() => import("../components/package/PackageList")));

const PackageTypeList = Loadable(lazy(() => import("../components/packageType/PackageTypeList")));
const PackageTypeDetails = Loadable(lazy(() => import("../components/packageType/PackageTypeDetails")));
const PackageType = Loadable(lazy(() => import("../components/packageType/PackageType")));

const IpLogSearch = Loadable(lazy(() => import("../components/iplog/IpLogSearch")));
const IpLogPdf = Loadable(lazy(() => import("../components/iplog/pdf/IpLogPdf")));

const Login = Loadable(lazy(() => import('../views/auth/Login')));

const Analytical = Loadable(lazy(() => import('../components/dashboard/Analytical')));
const UploadList = Loadable(lazy(() => import('../components/upload/UploadList')));
const InvoiceSyncCountReport = Loadable(lazy(() => import('../components/report/InvoiceSyncCountReport')));
const CustomerActivationReport = Loadable(lazy(() => import('../components/report/CustomerActivationReport')));

const CachedCompanyList = Loadable(lazy(() => import('../components/cache/CachedCompanyList')));
const CachedServerList = Loadable(lazy(() => import("../components/cache/CachedServerList")));
const CachedZoneList = Loadable(lazy(() => import("../components/cache/CachedZoneList")));
const CachedPackageList = Loadable(lazy(() => import('../components/cache/CachedPackageList')));
const CachedTaxesList = Loadable(lazy(() => import('../components/cache/CachedTaxesList')));
const CachedCommissionRulesList = Loadable(lazy(() => import('../components/cache/CachedCommissionRulesList')));

const Customer = Loadable(lazy(() => import('../components/customer/Customer')));
const ConfigList = Loadable(lazy(() => import('../components/config/ConfigList')));
const BuildInfoList = Loadable(lazy(() => import('../components/config/BuildInfoList')));
const DataRestrictionList = Loadable(lazy(() => import('../components/config/DataRestrictionList')));
const PackageWhitelist = Loadable(lazy(() => import('../components/config/PackageWhitelist')));

const PaymentGatewayList = Loadable(lazy(() => import('../components/paymentGateway/PaymentGatewayList')));
const PgClientApplicationList = Loadable(lazy(() => import('../components/paymentGateway/PgClientApplicationList')));

const UserGroupList = Loadable(lazy(() => import('../components/user/UserGroupList')));
const UserGroupForm = Loadable(lazy(() => import('../components/user/UserGroupForm')));
const LaunchpadUserList = Loadable(lazy(() => import('../components/user/LaunchpadUserList')));
const LaunchpadUserForm = Loadable(lazy(() => import('../components/user/LaunchpadUserForm')));
const PartnerUserList = Loadable(lazy(() => import('../components/user/PartnerUserList')));
const PartnerUserCreate = Loadable(lazy(() => import('../components/user/PartnerUserCreate')));
const PartnerUserUpdate = Loadable(lazy(() => import('../components/user/PartnerUserUpdate')));
const AuditHistory = Loadable(lazy(() => import('../components/user/UserAuditHistory')));

// Network Setup
const NetworkList = Loadable(lazy(() => import('../components/network/NetworkList')));
const AddSwitch = Loadable(lazy(() => import('../components/network/AddSwitch')));
const EditSwitch = Loadable(lazy(() => import('../components/network/EditSwitch')));
const NetworkSetupForm = Loadable(lazy(() => import('../components/network/NetworkSetupForm')));
const SwitchDetails = Loadable(lazy(() => import('../components/network/SwitchDetails')));
const PortVlanMapping = Loadable(lazy(() => import('../components/network/PortVlanMapping')));
const ServerVlanMapping = Loadable(lazy(() => import('../components/network/ServerVlanMapping')));
const IpBlockManagement = Loadable(lazy(() => import('../components/network/IpBlockManagement')));
const AddZonePool = Loadable(lazy(() => import('../components/network/AddZonePool')));
const IpBlockList = Loadable(lazy(() => import('../components/network/IpBlockList')));
const NetworkHierarchyView = Loadable(lazy(() => import('../components/network/NetworkHierarchyView')));


/***** Auth Pages ****/
const Error = Loadable(lazy(() => import("../views/auth/Error")));
const Unauthorized = Loadable(lazy(() => import("../views/auth/Unauthorized")));

/*****Routes******/

const ThemeRoutes = [
  {
    path: "/",
    element: <FullLayout />,
    errorElement: <Error />,
    children: [
      { path: '*', element: <Navigate to="/error" /> },
      { path: '/', element: <Navigate to="/login" /> },
      {
        path: "/company",
        children: [
          { path: '', element: <Navigate to="/company/list" /> },
          { path: 'add', element: <Company /> },
          { path: 'edit/:id', element: <Company /> },
          { path: 'list', element: <CompanyList /> },
          { path: 'details/:id', element: <CompanyDetails /> },
          { path: '*', element: <Navigate to="/company/list" /> },
        ]
      },
      {
        path: "/cache",
        children: [
          { path: 'companies/list', element: <CachedCompanyList /> },
          { path: 'servers/list', element: <CachedServerList /> },
          { path: 'zones/list', element: <CachedZoneList /> },
          { path: 'packages/list', element: <CachedPackageList /> },
          { path: 'taxes/list', element: <CachedTaxesList /> },
          { path: 'commission/list', element: <CachedCommissionRulesList /> },
          { path: '*', element: <Navigate to="/cache/companies/list" /> },
        ]
      },
      {
        path: "/lookup",
        children: [
          { path: 'customer', element: <CustomerSearch /> },
          /*{ path: 'customer', element: <Customer /> },*/
          { path: 'customer/:id', element: <Customer /> },
          { path: '*', element: <Navigate to="/lookup/customer" /> },
        ]
      },
      {
        path: "/config",
        children: [
          { path: '', element: <Navigate to="/config/list" /> },
          { path: 'list', element: <ConfigList /> },
          { path: '*', element: <Navigate to="/config/list" /> },
          { path: 'data-restriction', element: <DataRestrictionList /> },
          { path: 'package-whitelist', element: <PackageWhitelist /> },
        ]

      },
      {
        path: "/build-info",
        children: [
          { path: '', element: <Navigate to="/build-info/list" /> },
          { path: 'list', element: <BuildInfoList /> },
          { path: '*', element: <Navigate to="/build-info/list" /> },
        ]

      },
      {
        path: "/network",
        children: [
          { path: '', element: <Navigate to="/network/list" /> },
          { path: 'list', element: <IpBlockList /> },
          { path: 'hierarchy', element: <NetworkHierarchyView /> },
          { path: 'switch-list', element: <Navigate to="/network/hierarchy" /> }, // legacy redirect
          { path: 'switch-table', element: <NetworkList /> },
          { path: 'add-switch', element: <AddSwitch /> },
          { path: 'edit-switch/:id', element: <EditSwitch /> },
          { path: 'add', element: <NetworkSetupForm /> },
          { path: 'switch/:id', element: <SwitchDetails /> },
          { path: 'port-vlan-mapping', element: <PortVlanMapping /> },
          { path: 'nas-vlan-mapping', element: <ServerVlanMapping /> },
          { path: 'server-vlan-mapping', element: <ServerVlanMapping /> }, // legacy route
          { path: 'ip-block-management', element: <IpBlockManagement /> },
          { path: 'add-zone-pool', element: <AddZonePool /> },
          { path: 'ip-block-list', element: <IpBlockList /> },
          { path: '*', element: <Navigate to="/network/list" /> },
        ]
      },
      {
        path: "/distributor",
        children: [
          { path: '', element: <Navigate to="/distributor/list" /> },
          { path: 'add', element: <Distributor /> },
          { path: 'edit/:id', element: <Distributor /> },
          { path: 'list', element: <DistributorList /> },
          { path: 'details/:id', element: <DistributorDetails /> },
          { path: '*', element: <Navigate to="/distributor/list" /> },
        ]
      },
      {
        path: "/serverGroup",
        children: [
          { path: '', element: <Navigate to="/serverGroup/list" /> },
          { path: 'add', element: <ServerGroup /> },
          { path: 'edit/:id', element: <ServerGroup /> },
          { path: 'list', element: <ServerGroupList /> },
          { path: 'details/:id', element: <ServerGroupDetails /> },
          { path: '*', element: <Navigate to="/serverGroup/list" /> },
        ]
      },
      {
        path: "/zoneGroup",
        children: [
          { path: '', element: <Navigate to="/zoneGroup/list" /> },
          { path: 'add', element: <ZoneGroup /> },
          { path: 'edit/:id', element: <ZoneGroup /> },
          { path: 'list', element: <ZoneGroupList /> },
          { path: 'details/:id', element: <ZoneGroupDetails /> },
          { path: '*', element: <Navigate to="/zoneGroup/list" /> },
        ]
      },
      {
        path: "/zone",
        children: [
          { path: '', element: <Navigate to="/zone/list" /> },
          { path: 'add', element: <ZoneAdd /> },
          { path: 'statement', element: <ZoneStatement /> },
          { path: 'commissionRule', element: <CommissionRuleList /> },
          { path: 'commissionRule/:code', element: <CommissionRuleDetails /> },
          { path: 'commissionSlab/:code', element: <CommissionSlabDetails /> },
          { path: 'renew', element: <ZoneRenew /> },
          { path: 'commission', element: <ZoneCommission /> },
          { path: 'commission/v2', element: <ZoneCommissionV2 /> },
          { path: 'balance', element: <ZoneBalance /> },
          { path: 'allBalance', element: <AllZoneBalance /> },
          { path: 'edit/:id', element: <ZoneEdit /> },
          { path: 'list', element: <ZoneList /> },
          { path: 'details/:id', element: <ZoneDetails /> },
          { path: 'adjustment', element: <ZoneAdjustment /> },
          { path: 'transactions', element: <ZoneTransactions /> },
          { path: 'wallet/recharge', element: <ZoneWalletRecharge /> },
          { path: '*', element: <Navigate to="/zone/list" /> },
        ]
      },
      {
        path: "/nas",
        children: [
          { path: '', element: <Navigate to="/nas/list" /> },
          { path: 'add', element: <NasAdd /> },
          { path: 'edit/:id', element: <NasEdit /> },
          { path: 'list', element: <NasList /> },
          { path: 'zone-mapping', element: <NasZoneMappingList /> },
          { path: 'package-mapping', element: <NasPackageZoneMappingList /> },
          { path: 'zone-nas-wise-packages', element: <AssignedPackagesList /> },
          { path: '*', element: <Navigate to="/nas/list" /> },
        ]
      },
      {
        path: "/value-added-services",
        children: [
          { path: '', element: <Navigate to="/value-added-services/list" /> },
          { path: 'list', element: <ValueAddedServiceList /> },
          { path: 'ott', element: <ValueAddedServiceList key="ott" /> },
          { path: 'iptv', element: <ValueAddedServiceList key="iptv" /> },
          { path: 'aggregator', element: <ValueAddedServiceList key="aggregator" /> },
          { path: 'coupon', element: <ValueAddedServiceList key="coupon" /> },
          { path: 'add', element: <ValueAddedServiceAdd /> },
          { path: 'add-ott', element: <ValueAddedServiceAdd key="add-ott" /> },
          { path: 'add-iptv', element: <ValueAddedServiceAdd key="add-iptv" /> },
          { path: 'add-aggregator', element: <ValueAddedServiceAdd key="add-aggregator" /> },
          { path: 'add-coupon', element: <ValueAddedServiceAdd key="add-coupon" /> },
          { path: 'edit/:id', element: <ValueAddedServiceEdit /> },
          { path: 'edit-ott/:id', element: <ValueAddedServiceEdit key="edit-ott" /> },
          { path: 'edit-iptv/:id', element: <ValueAddedServiceEdit key="edit-iptv" /> },
          { path: 'edit-aggregator/:id', element: <ValueAddedServiceEdit key="edit-aggregator" /> },
          { path: 'edit-coupon/:id', element: <ValueAddedServiceEdit key="edit-coupon" /> },
          { path: '*', element: <Navigate to="/value-added-services/list" /> },
        ]
      },
      {
        path: "/ott/vas-provider-packages",
        children: [
          { path: '', element: <Navigate to="/ott/vas-provider-packages/list" /> },
          { path: 'list', element: <VASProviderPackageList /> },
          { path: 'add', element: <VASProviderPackageAdd /> },
          { path: 'edit/:id', element: <VASProviderPackageEdit /> },
          { path: '*', element: <Navigate to="/ott/vas-provider-packages/list" /> },
        ]
      },
      {
        path: "/ott/vas-aggregates",
        children: [
          { path: '', element: <Navigate to="/ott/vas-aggregates/list" /> },
          { path: 'list', element: <VASAggregateList /> },
          { path: 'add', element: <VASAggregateAdd /> },
          { path: 'edit/:id', element: <VASAggregateEdit /> },
          { path: '*', element: <Navigate to="/ott/vas-aggregates/list" /> },
        ]
      },
      { path: "/ott/bundles",
        children: [
          { path: '', element: <Navigate to="/ott/bundles/list" /> },
          { path: 'list', element: <BundleList /> },
          { path: 'add', element: <BundleAdd /> },
          { path: 'edit/:id', element: <BundleEdit /> },
          { path: 'configure/:id', element: <BundleConfigure /> },
          { path: '*', element: <Navigate to="/ott/bundles/list" /> },
        ]
      },
      { path: "/ott/auto-eligibility",
        children: [
          { path: '', element: <Navigate to="/ott/auto-eligibility/list" /> },
          { path: 'list', element: <AutoEligibilityList /> },
          { path: 'add', element: <AutoEligibilityAdd /> },
          { path: 'edit/:id', element: <AutoEligibilityEdit /> },
          { path: '*', element: <Navigate to="/ott/auto-eligibility/list" /> },
        ]
      },
      { path: "/ott/customers",
        children: [
          { path: '', element: <Navigate to="/ott/customers/list" /> },
          { path: 'list', element: <OttCustomerList /> },
          { path: '*', element: <Navigate to="/ott/customers/list" /> },
        ]
      },
      { path: "/ott/manual-console", element: <ManualConsole /> },
      { path: "/ott/coupons",
        children: [
          { path: '', element: <Navigate to="/ott/coupons/list" /> },
          { path: 'list', element: <CouponList /> },
          { path: 'upload', element: <CouponUpload /> },
          { path: '*', element: <Navigate to="/ott/coupons/list" /> },
        ]
      },
      { path: "/ott/source-config",
        children: [
          { path: '', element: <Navigate to="/ott/source-config/list" /> },
          { path: 'list', element: <SourceConfigList /> },
          { path: 'add', element: <SourceConfigAdd /> },
          { path: 'edit/:id', element: <SourceConfigEdit /> },
          { path: '*', element: <Navigate to="/ott/source-config/list" /> },
        ]
      },
      { path: "/ott/audit",
        children: [
          { path: '', element: <Navigate to="/ott/audit/list" /> },
          { path: 'list', element: <AuditLogList /> },
          { path: 'entity/:entityType/:entityId', element: <EntityAuditHistory /> },
          { path: '*', element: <Navigate to="/ott/audit/list" /> },
        ]
      },
      { path: "/legalOffice",
        children: [
          { path: '', element: <Navigate to="/legalOffice/list" /> },
          { path: 'add', element: <LegalOffice /> },
          { path: 'edit/:id', element: <LegalOffice /> },
          { path: 'list', element: <LegalOfficeList /> },
          { path: 'details/:id', element: <LegalOfficeDetails /> },
          { path: '*', element: <Navigate to="/legalOffice/list" /> },
        ]
      },
      {
        path: "/supportOffice",
        children: [
          { path: '', element: <Navigate to="/supportOffice/list" /> },
          { path: 'add', element: <SupportOffice /> },
          { path: 'edit/:id', element: <SupportOffice /> },
          { path: 'list', element: <SupportOfficeList /> },
          { path: 'details/:id', element: <SupportOfficeDetails /> },
          { path: '*', element: <Navigate to="/supportOffice/list" /> },
        ]
      },
      {
        path: "/server",
        children: [
          { path: '', element: <Navigate to="/server/list" /> },
          { path: 'add', element: <Server /> },
          { path: 'edit/:id', element: <Server /> },
          { path: 'list', element: <ServerList /> },
          { path: 'details/:id', element: <ServerDetails /> },
          { path: '*', element: <Navigate to="/server/list" /> },
        ]
      },
      {
        path: "/packageType",
        children: [
          { path: '', element: <Navigate to="/zoneGroup/list" /> },
          { path: 'add', element: <PackageType /> },
          { path: 'edit/:id', element: <PackageType /> },
          { path: 'list', element: <PackageTypeList /> },
          { path: 'details/:id', element: <PackageTypeDetails /> },
          { path: '*', element: <Navigate to="/packageType/list" /> },
        ]
      },
      {
        path: "/packages",
        children: [
          { path: '', element: <Navigate to="/zoneGroup/list" /> },
          { path: 'add', element: <PackageAdd /> },
          { path: 'edit/:id', element: <PackageDetails /> },
          { path: 'list', element: <PackageList /> },
          { path: '*', element: <Navigate to="/packages/list" /> },
        ]
      },
      {
        path: "/payment-gateways",
        children: [
          { path: 'list', element: <PaymentGatewayList /> },
          { path: 'client-application/list', element: <PgClientApplicationList /> },
        ]
      },
      {
        path: "/iplog",
        children: [
          { path: 'search', element: <IpLogSearch /> },
          { path: '*', element: <Navigate to="/iplog/search" /> },
        ]
      },
      {
        path: "/dashboard",
        children: [
          { path: 'analytical', element: <Analytical /> },
          { path: '*', element: <Navigate to="/dashboard/analytical" /> },
        ]
      },
      {
        path: "/bulk-upload",
        children: [
          { path: '', element: <UploadList /> },
          { path: '*', element: <Navigate to="/bulk-upload" /> },
        ]
      },
      {
        path: "/report",
        children: [
          { path: '', element: < CustomerActivationReport /> },
          { path: 'orphan-invoices', element: < OrphanInvoices /> },
          { path: 'sync-invoice-count', element: < InvoiceSyncCountReport /> },
          { path: 'customer-activation', element: < CustomerActivationReport /> },
          { path: '*', element: <Navigate to="report/customer-activation" /> },
        ]
      },
      {
        path: "/renewal/transactions",
        children: [
          { path: '', element: <RenewalTransactions /> }
        ]
      },
      {
        path: "/topup/transactions",
        children: [
          { path: '', element: <TopupTransactions /> }
        ]
      },
      {
        path: "/notifications",
        children: [
          { path: '', element: <Navigate to="/notifications/dashboard" /> },
          { path: 'dashboard', element: <NotificationDashboard /> },
          { path: 'templates', element: <NotificationTemplates /> },
          { path: 'routes', element: <NotificationRoutes /> },
          { path: 'email-config', element: <NotificationEmailConfig /> },
          { path: 'sms-config', element: <NotificationSmsConfig /> },
          { path: 'push-config', element: <NotificationPushConfig /> },
          { path: 'send', element: <NotificationSend /> },
          { path: 'tracking', element: <NotificationTracking /> },
          { path: '*', element: <Navigate to="/notifications/dashboard" /> },
        ]
      },
      {
        path: "/identity",
        children: [
          { path: '', element: <Navigate to="/identity/users" /> },
          { path: 'users', element: <LaunchpadUserList /> },

          { path: 'user/group', element: <UserGroupList /> },
          { path: 'user/group/create', element: <UserGroupForm /> },
          { path: 'user/group/edit/:id', element: <UserGroupForm /> },

          { path: 'user/create/v2', element: <LaunchpadUserForm key="create" /> },
          { path: 'user/edit/v2/:userId', element: <LaunchpadUserForm key="edit" /> },
          { path: 'audit-history', element: <AuditHistory /> },
          { path: '*', element: <Navigate to="/identity/users" /> },
        ]
      },
      {
        path: "/partner/user",
        children: [
          { path: '', element: <Navigate to="/partner/user/list" /> },
          { path: 'list', element: <PartnerUserList /> },
          { path: 'create', element: <PartnerUserCreate /> },
          { path: 'edit/:username', element: <PartnerUserUpdate /> },
          { path: '*', element: <Navigate to="/partner/user/list" /> },
        ]
      },
    ],
  },
  { path: "/callback", element: <OAuthPopup /> },
  {
    path: "/auth",
    element: <BlankLayout />,
    children: [
      { path: "404", element: <Error /> },
      { path: "*", element: <Navigate to="/auth/404" /> },
    ],
  },
  {
    path: "/iplog",
    element: <BlankLayout />,
    children: [
      { path: 'pdf', element: <IpLogPdf /> },
    ]
  },
  {
    path: "/login",
    element: <Login />
  },
  {
    path: "/unauthorized",
    element: <Unauthorized />
  },
  {
    path: "/error",
    element: <Error />
  }
];

export default ThemeRoutes;
